import React from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../../api/client';
import { useApi } from '../../api/useApi';
import { STATUS_BADGE } from '../philData';

export default function PhilDashboard() {
  const navigate = useNavigate();
  const { data, loading, error } = useApi(() => API.applicantDashboard());

  if (loading) return <div className="phil-loading">Loading…</div>;
  if (error || !data) return <div className="phil-error">Unable to load dashboard. {error}</div>;

  return (
    <>
      <div className="phil-page-heading">Welcome, {data.applicantName}</div>
      <div className="phil-page-sub">{data.dateSubtitle}</div>

      <div className="phil-program-blurb">{data.programBlurb}</div>

      <div className="phil-kpi-row">
        <div className="phil-kpi-card">
          <div className="phil-kpi-number">{data.percentComplete}%</div>
          <div className="phil-kpi-label">Application Complete</div>
        </div>
        <div className="phil-kpi-card">
          <div className="phil-kpi-number">{data.sectionsRemaining}</div>
          <div className="phil-kpi-label">Sections Remaining</div>
        </div>
        <div className="phil-kpi-card">
          <div className="phil-kpi-number">{data.submissionDeadline}</div>
          <div className="phil-kpi-label">Submission Deadline</div>
        </div>
      </div>

      <div className="phil-card">
        <div className="phil-card-head">
          <h2>Action Items</h2>
          <span className="phil-section-sub" onClick={() => navigate('/apply/review')}>View all →</span>
        </div>
        <table className="phil-table">
          <thead>
            <tr>
              <th>Section</th>
              <th>Task</th>
              <th>Detail</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.actionItems.length === 0 && (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', padding: '24px 0', color: '#888' }}>
                  No outstanding action items at this time.
                </td>
              </tr>
            )}
            {data.actionItems.map((item: any, i: number) => (
              <tr
                key={i}
                className="phil-row-link"
                onClick={() => navigate(`/apply/${sectionRoute(item.sectionId)}`)}
              >
                <td>{item.section}</td>
                <td style={{ fontWeight: 600 }}>{item.task}</td>
                <td style={{ color: '#555' }}>{item.detail}</td>
                <td><span className={STATUS_BADGE[item.status] || 'phil-badge phil-badge-draft'}>{item.status}</span></td>
                <td className="phil-arrow">→</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.submitted ? (
        <div className="phil-submitted-banner" style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="phil-badge phil-badge-complete">Submitted</span>
          <span>
            Your application has been submitted{data.submittedAt ? ` on ${formatSubmittedAt(data.submittedAt)}` : ''}.
          </span>
          <button
            className="phil-btn phil-btn-secondary"
            style={{ marginLeft: 'auto' }}
            onClick={() => navigate('/apply/review')}
          >
            View Application →
          </button>
        </div>
      ) : (
        <div style={{ marginTop: 20, display: 'flex', gap: 10 }}>
          <button className="phil-btn phil-btn-primary" onClick={() => navigate('/apply/section-1')}>
            Continue Application →
          </button>
          <button className="phil-btn phil-btn-secondary" onClick={() => navigate('/apply/review')}>
            Review &amp; Submit
          </button>
        </div>
      )}

      <div style={{ marginTop: 32, color: '#555', fontSize: '0.875rem' }}>
        If you have any questions, please contact{' '}
        <a href="mailto:fil.nyc@accenture.com">fil.nyc@accenture.com</a>
      </div>
    </>
  );
}

// Format an ISO submission timestamp for display (e.g. "29 Jun 2026").
function formatSubmittedAt(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Map a section id to its 1-based form route (section-N).
function sectionRoute(sectionId: string): string {
  const order = ['company-basics', 'tech-infra', 'financials', 'demographics', 'supplementary'];
  const idx = order.indexOf(sectionId);
  return `section-${idx >= 0 ? idx + 1 : 1}`;
}
